import type { QueryClient } from '@tanstack/react-query';
import type { DependencyType, Item } from '@/lib/api/types/items';
import type {
  ScenarioItem,
  TimelineItemItemDependency,
} from '@/lib/api/types/loop-planner/timeline-item';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { insightsQueryKeys } from '@/lib/query-keys/insights';
import { loopPlannerQueryKeys } from '@/lib/query-keys/loop-planner';
import { loopVisualizationQueryKeys } from '@/lib/query-keys/loop-visualizations';

export function patchItemCache(
  queryClient: QueryClient,
  itemId: number,
  patch: Partial<Item>,
): Item | undefined {
  const snapshot = queryClient.getQueryData<Item>(commonQueryKeys.item(itemId));
  if (snapshot) {
    queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), (old) =>
      old ? ({ ...old, ...patch } as Item) : old,
    );
  }
  return snapshot;
}

export function restoreItemCache(
  queryClient: QueryClient,
  itemId: number,
  snapshot: Item | undefined,
): void {
  if (snapshot !== undefined) {
    queryClient.setQueryData(commonQueryKeys.item(itemId), snapshot);
  }
}

export function invalidateSingleItemQueries(queryClient: QueryClient, itemId: number): void {
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(itemId) });
  void queryClient.invalidateQueries({
    queryKey: loopVisualizationQueryKeys.loopItem.detail(itemId),
  });
  void queryClient.invalidateQueries({
    queryKey: loopVisualizationQueryKeys.items.partial(),
    refetchType: 'none',
  });
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetchV2.all() });
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetch.all() });
  void queryClient.invalidateQueries({
    queryKey: [commonQueryKeys.itemsTable.visibleItems],
    refetchType: 'none',
  });
}

export function invalidateAfterDependencyChange(
  queryClient: QueryClient,
  itemAId: number,
  itemBId: number,
): void {
  invalidateSingleItemQueries(queryClient, itemAId);
  invalidateSingleItemQueries(queryClient, itemBId);
  void queryClient.invalidateQueries({ queryKey: insightsQueryKeys.health.byItemId(itemAId) });
  void queryClient.invalidateQueries({ queryKey: insightsQueryKeys.health.byItemId(itemBId) });
}

export function invalidateAfterScenarioDependencyChange(
  queryClient: QueryClient,
  loopId: number,
  scenarioId: number,
): void {
  void queryClient.invalidateQueries({
    queryKey: loopPlannerQueryKeys.timelineItems.root({ loopId, scenarioId }),
  });
}

interface PatchScenarioItemDependencyParams {
  queryClient: QueryClient;
  loopId: number;
  scenarioId: number;
  itemId: number;
  otherItemId: number;
  dependencyType: DependencyType;
  action: 'add' | 'remove';
}

// Adds or removes a single dependency edge on the cached scenario item, keyed
// from the perspective of `itemId`. The scenario table subscribes to this cache
// and syncs the change into its own (instance-scoped) visible rows, so the
// dependency indicator updates without a refetch.
export function patchScenarioItemDependency({
  queryClient,
  loopId,
  scenarioId,
  itemId,
  otherItemId,
  dependencyType,
  action,
}: PatchScenarioItemDependencyParams): void {
  const key = loopPlannerQueryKeys.scenarioItems.detail({ loopId, scenarioId, itemId });
  queryClient.setQueryData<ScenarioItem>(key, (existing) => {
    if (!existing) return existing;
    const withoutEdge = (existing.dependencies ?? []).filter(
      (dep) => !(dep.dependencyItemId === otherItemId && dep.dependencyType === dependencyType),
    );
    const nextDependencies: TimelineItemItemDependency[] =
      action === 'add'
        ? [...withoutEdge, { dependencyType, dependencyItemId: otherItemId }]
        : withoutEdge;
    return { ...existing, dependencies: nextDependencies };
  });
}
